// ============================================================================
// SPECIALIST WORKLIST VIEW
//
// Roles: specialist, specialist_admin (RouteGuard enforces before this renders).
//
// THREE TABS — default landing = Today:
//
//   Today        — SCHEDULED referrals where appointmentDate = today
//                  Sort: time asc → priority desc → referralId asc
//
//   Needs Action — SCHEDULED referrals where appointmentDate < today (overdue)
//                  Sort: oldest date first → priority desc → referralId asc
//
//   Upcoming     — SCHEDULED referrals where appointmentDate > today
//                  Sort: soonest date first → priority desc → referralId asc
//
// All three tabs share the same data load (one listWorkqueueItems call filtered
// client-side) — no separate queries needed; the list is small per tenant.
//
// Visibility rules (BOTH must be true for a referral to appear):
//   1. referral.specialtyId ∈ specialist's user.specialtyIds array
//   2. workflow.scheduling.consultantId === specialist's uid
//
// Primary action on Today + Needs Action cards:
//   "Close Referral Loop" → completeConsult()
//   → writes SCHEDULED→SEEN event + SEEN→CLOSED_LOOP event atomically
//   → final workflow/state.status = CLOSED_LOOP
//
// Clicking any card opens ConsultDetailDrawer with full referral + PHI data.
//
// Zero-PHI invariant: no patient name/DOB/MRN in workflow docs or list items.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  Stethoscope, Loader2, AlertCircle, RefreshCw,
  Calendar, User, CheckCircle2, X, ClipboardList,
  ChevronRight, FileText, Activity, Clock, Eye,
  Hash, Shield, AlertTriangle, RotateCcw,
  GitBranch, Phone, CreditCard, Paperclip,
  Lock, Inbox, MapPin, Upload, UserX, Flame,
} from 'lucide-react';
import {
  WORKFLOW_STATUS,
  WORKFLOW_PRIORITY,
  WORKFLOW_EVENT_TYPES,
  ROLE_DISPLAY,
  listWorkqueueItems,
  listWorkflowEvents,
  REWORK_ITEM_TYPE_LABELS,
  REWORK_REQUEST_STATUS,
  listReworkRequests,
  getUser,
  getPatientIdentity,
  getContact,
  getInsurance,
  listAttachments,
  createAttachmentUploadUrl,
  finalizeAttachmentUpload,
} from '../core/index.js';
import { getReferral } from '../core/store.referrals.js';
import { findReferralInBuckets, classifyDeepLinkFailure } from '../core/deepLinkReferral.js';
import { completeConsult, markAsSeen, closeLoop, markAsNoShow } from '../core/store.scheduling.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { navigateToAttachmentViewer } from '../components/AttachmentPdfViewer.jsx';

// ── Constants ──────────────────────────────────────────────────────────────────

const TODAY_STR = new Date().toISOString().slice(0, 10); // YYYY-MM-DD, local-ish

// ── Display config ─────────────────────────────────────────────────────────────

const PRIORITY_CONFIG = {
  [WORKFLOW_PRIORITY.NORMAL]: { label: 'Normal', tw: 'bg-slate-700/60 text-slate-400 border-slate-600/30' },
  [WORKFLOW_PRIORITY.HIGH]:   { label: 'High',   tw: 'bg-orange-500/15 text-orange-300 border-orange-500/20' },
  [WORKFLOW_PRIORITY.URGENT]: { label: 'Urgent', tw: 'bg-red-500/15 text-red-300 border-red-500/20' },
};

const PRIORITY_RANK = { [WORKFLOW_PRIORITY.URGENT]: 0, [WORKFLOW_PRIORITY.HIGH]: 1, [WORKFLOW_PRIORITY.NORMAL]: 2 };

const OUTCOME_CONFIG = {
  URGENT:  { label: 'URGENT',  tw: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ROUTINE: { label: 'ROUTINE', tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
  NOT_YET: { label: 'NOT YET', tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
  urgent:  { label: 'URGENT',  tw: 'bg-red-500/20 text-red-300 border-red-500/30' },
  routine: { label: 'ROUTINE', tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
  not_yet: { label: 'NOT YET', tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
};

const STATUS_CONFIG = {
  NEW:               { label: 'New',               tw: 'bg-blue-500/15 text-blue-300 border-blue-500/20' },
  NEEDS_INFO:        { label: 'Needs Info',         tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
  READY_TO_SCHEDULE: { label: 'Ready to Schedule',  tw: 'bg-teal-500/15 text-teal-300 border-teal-500/20' },
  SCHEDULED:         { label: 'Scheduled',          tw: 'bg-blue-500/15 text-blue-300 border-blue-500/20' },
  SEEN:              { label: 'Seen',               tw: 'bg-purple-500/15 text-purple-300 border-purple-500/20' },
  CLOSED_LOOP:       { label: 'Closed Loop',        tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
};

const REWORK_STATUS_CONFIG = {
  [REWORK_REQUEST_STATUS.OPEN]:      { label: 'Open',      tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
  [REWORK_REQUEST_STATUS.RECEIVED]:  { label: 'Received',  tw: 'bg-teal-500/15 text-teal-300 border-teal-500/20' },
  [REWORK_REQUEST_STATUS.RESOLVED]:  { label: 'Resolved',  tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
  [REWORK_REQUEST_STATUS.CANCELLED]: { label: 'Cancelled', tw: 'bg-slate-700/60 text-slate-500 border-slate-600/30' },
};

const EVENT_CONFIG = {
  [WORKFLOW_EVENT_TYPES.WORKFLOW_CREATED]: { label: 'Referral Created',       icon: FileText,     color: 'text-slate-400'  },
  [WORKFLOW_EVENT_TYPES.OWNER_ASSIGNED]:   { label: 'Claimed by Coordinator', icon: User,         color: 'text-blue-400'   },
  [WORKFLOW_EVENT_TYPES.OWNER_UNASSIGNED]: { label: 'Owner Released',         icon: User,         color: 'text-slate-400'  },
  [WORKFLOW_EVENT_TYPES.STATUS_CHANGED]:   { label: 'Status Changed',         icon: Activity,     color: 'text-teal-400'   },
  [WORKFLOW_EVENT_TYPES.TRIAGE_UPDATED]:   { label: 'Triage Updated',         icon: Stethoscope,  color: 'text-purple-400' },
  [WORKFLOW_EVENT_TYPES.SCHEDULED_SET]:    { label: 'Appointment Scheduled',  icon: Calendar,     color: 'text-blue-400'   },
  [WORKFLOW_EVENT_TYPES.SEEN_SET]:         { label: 'Marked Seen',            icon: Eye,          color: 'text-teal-400'   },
  [WORKFLOW_EVENT_TYPES.CLOSED_LOOP_SET]:  { label: 'Consult Completed',      icon: CheckCircle2, color: 'text-emerald-400'},
  PRIORITY_CHANGED:                        { label: 'Priority Changed',        icon: AlertTriangle,color: 'text-orange-400' },
};

const VISIT_TYPE_LABELS = {
  initial_consultation: 'Initial Consultation',
  follow_up:            'Follow Up',
  second_opinion:       'Second Opinion',
  urgent_review:        'Urgent Review',
};

const VISIT_PURPOSE_LABELS = {
  new_complaint:      'New Complaint',
  ongoing_management: 'Ongoing Management',
  post_procedure:     'Post Procedure',
  pre_procedure:      'Pre Procedure',
  other:              'Other',
};

const TRIAGE_DECISION_LABELS = {
  none:         'None',
  ok:           'OK',
  urgent_review:'Urgent Review',
  redirect:     'Redirect',
};

const ATTACHMENT_TYPE_LABELS = {
  LABS:              'Lab Results',
  IMAGING:           'Imaging',
  PDF:               'PDF Document',
  PHOTO:             'Photo',
  OTHER:             'Document',
  CONSULT_DOCUMENT:  'Consult Document',
};

// ── Tab definitions ────────────────────────────────────────────────────────────

const TABS = [
  {
    id:          'today',
    label:       'Today',
    activeColor: 'text-teal-400 border-teal-500',
    emptyIcon:   Calendar,
    emptyTitle:  'No appointments today',
    emptyBody:   'Scheduled referrals for today will appear here.',
    learningTarget: 'specialist-today-tab',
  },
  {
    id:          'needs_action',
    label:       'Needs Action',
    activeColor: 'text-amber-400 border-amber-500',
    emptyIcon:   CheckCircle2,
    emptyTitle:  'All caught up',
    emptyBody:   'Past appointments that still need referral closure will appear here.',
    learningTarget: 'specialist-needs-action-tab',
  },
  {
    id:          'upcoming',
    label:       'Upcoming',
    activeColor: 'text-blue-400 border-blue-500',
    emptyIcon:   Inbox,
    emptyTitle:  'No upcoming appointments',
    emptyBody:   'Future scheduled referrals will appear here.',
  },
  {
    id:          'closed_referrals',
    label:       'Closed Referrals',
    activeColor: 'text-emerald-400 border-emerald-500',
    emptyIcon:   CheckCircle2,
    emptyTitle:  'No closed referrals',
    emptyBody:   'Referrals you have completed will appear here.',
  },
];

// ── Closed sort ────────────────────────────────────────────────────────────────

function tsSec(ts) {
  if (!ts) return null;
  if (typeof ts.seconds === 'number') return ts.seconds;
  if (typeof ts.toMillis === 'function') return ts.toMillis() / 1000;
  return null;
}

function sortClosed(arr) {
  return [...arr].sort((a, b) => {
    const ca = tsSec(a.workflow?.closedLoop?.closedAt);
    const cb = tsSec(b.workflow?.closedLoop?.closedAt);
    if (ca !== cb) { if (ca === null) return 1; if (cb === null) return -1; return cb - ca; }
    const sa = tsSec(a.workflow?.scheduling?.seenAt);
    const sb = tsSec(b.workflow?.scheduling?.seenAt);
    if (sa !== sb) { if (sa === null) return 1; if (sb === null) return -1; return sb - sa; }
    return (tsSec(b.workflow?.updatedAt) ?? 0) - (tsSec(a.workflow?.updatedAt) ?? 0);
  });
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtLabel(str) {
  if (!str) return '—';
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtTs(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function from24h(str) {
  if (!str) return null;
  const [h, m] = str.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return str;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour   = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

function fmtAnswerValue(val) {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) {
    if (val.length === 0) return '—';
    return val.map((v) => fmtLabel(String(v))).join(', ');
  }
  if (typeof val === 'object' && val.seconds !== undefined) return fmtTs(val);
  return fmtLabel(String(val));
}

function fmtPatientName(name) {
  if (!name) return null;
  if (typeof name === 'string') return name;
  const first = name.first ?? (Array.isArray(name.given) ? name.given.join(' ') : (name.given ?? ''));
  const last  = name.last  ?? name.family ?? '';
  return [first, last].filter(Boolean).join(' ') || null;
}

function fmtAddress(addr) {
  if (!addr) return null;
  if (typeof addr === 'string') return addr;
  const street     = addr.street ?? (Array.isArray(addr.line) ? addr.line.join(', ') : (addr.line ?? null));
  const postalCode = addr.zip    ?? addr.postalCode ?? null;
  return [street, addr.city, addr.state, postalCode, addr.country].filter(Boolean).join(', ') || null;
}

function fmtDob(dob) {
  if (!dob) return null;
  try {
    return new Date(dob + 'T00:00:00').toLocaleDateString(undefined, {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch { return dob; }
}

function fmtBytes(bytes) {
  if (!bytes || bytes < 1) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Returns today's date string YYYY-MM-DD in local time. */
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Classify a SCHEDULED item into today / needs_action / upcoming.
 *  Returns the tab id it belongs to, or null if it doesn't fit any tab. */
function classifyItem(item) {
  const apptDate = item.workflow?.scheduling?.appointmentDate ?? null;
  if (!apptDate) return null; // no date set — exclude from all tabs
  const today = todayLocal();
  if (apptDate === today) return 'today';
  if (apptDate < today)  return 'needs_action';
  return 'upcoming';
}

/** Priority sort rank — lower = more urgent. */
function priRank(item) {
  return PRIORITY_RANK[item.workflow?.priority] ?? 2;
}

/** Sort comparator for Today tab: time asc → priority asc → referralId asc */
function sortToday(a, b) {
  const tA = a.workflow?.scheduling?.appointmentTime ?? '';
  const tB = b.workflow?.scheduling?.appointmentTime ?? '';
  if (tA !== tB) return tA < tB ? -1 : 1;
  const pDiff = priRank(a) - priRank(b);
  if (pDiff !== 0) return pDiff;
  return (a.referral?.id ?? '').localeCompare(b.referral?.id ?? '');
}

/** Sort comparator for Needs Action: oldest date first → priority → id */
function sortNeedsAction(a, b) {
  const dA = a.workflow?.scheduling?.appointmentDate ?? '';
  const dB = b.workflow?.scheduling?.appointmentDate ?? '';
  if (dA !== dB) return dA < dB ? -1 : 1; // oldest first
  const pDiff = priRank(a) - priRank(b);
  if (pDiff !== 0) return pDiff;
  return (a.referral?.id ?? '').localeCompare(b.referral?.id ?? '');
}

/** Sort comparator for Upcoming: soonest first → priority → id */
function sortUpcoming(a, b) {
  const dA = a.workflow?.scheduling?.appointmentDate ?? '';
  const dB = b.workflow?.scheduling?.appointmentDate ?? '';
  if (dA !== dB) return dA < dB ? -1 : 1; // soonest first
  const tA = a.workflow?.scheduling?.appointmentTime ?? '';
  const tB = b.workflow?.scheduling?.appointmentTime ?? '';
  if (tA !== tB) return tA < tB ? -1 : 1;
  const pDiff = priRank(a) - priRank(b);
  if (pDiff !== 0) return pDiff;
  return (a.referral?.id ?? '').localeCompare(b.referral?.id ?? '');
}

// ── Shared sub-components ──────────────────────────────────────────────────────

function Badge({ tw, label }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${tw}`}>
      {label}
    </span>
  );
}

function DetailRow({ label, value, mono = false, dim = false }) {
  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className="text-xs text-slate-500 w-40 flex-shrink-0 pt-0.5 leading-relaxed">{label}</span>
      <span className={`text-xs flex-1 leading-relaxed break-words
        ${mono ? 'font-mono' : ''}
        ${dim ? 'text-slate-500 italic' : 'text-slate-200'}`}>
        {value ?? '—'}
      </span>
    </div>
  );
}

function SectionHeading({ icon: Icon, label, count, phi = false }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${phi ? 'text-amber-500/70' : 'text-slate-500'}`} />
      <span className={`text-[11px] font-semibold uppercase tracking-widest
        ${phi ? 'text-amber-500/70' : 'text-slate-500'}`}>
        {label}
      </span>
      {phi && (
        <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600/60 font-medium">
          <Lock className="w-2.5 h-2.5" /> PHI
        </span>
      )}
      {count != null && (
        <span className="ml-auto text-[11px] text-slate-600 font-medium">{count}</span>
      )}
    </div>
  );
}

function SectionCard({ children, phi = false }) {
  return (
    <div className={`rounded-xl border px-4 py-3 divide-y
      ${phi
        ? 'bg-amber-500/5 border-amber-500/20 divide-amber-500/10'
        : 'bg-slate-800/40 border-slate-700/30 divide-slate-700/20'}`}>
      {children}
    </div>
  );
}

function EmptyState({ message }) {
  return <p className="text-xs text-slate-600 italic py-2 px-1">{message}</p>;
}

function PhiLoadError({ onRetry }) {
  return (
    <div className="flex items-center gap-2 py-2 px-1">
      <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
      <span className="text-xs text-red-400">Failed to load PHI.</span>
      {onRetry && (
        <button onClick={onRetry} className="text-xs text-red-300 underline hover:no-underline ml-1">
          Retry
        </button>
      )}
    </div>
  );
}

// ── Attachment row ─────────────────────────────────────────────────────────────

function AttachmentRow({ tenantId, referralId, attachment }) {
  const attachmentId = attachment.attachmentId ?? attachment.id;
  const fileName     = attachment.originalName ?? null;
  const displayName  = fileName ?? attachment.type ?? 'Attachment';
  const typeLabel    = ATTACHMENT_TYPE_LABELS[attachment.type] ?? fmtLabel(attachment.type ?? 'Document');
  const size         = fmtBytes(attachment.sizeBytes);
  const isSystem     = attachment.source === 'SYSTEM_GENERATED';

  const btnBase   = 'flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border disabled:opacity-50';
  const btnNormal = `${btnBase} bg-slate-700/60 hover:bg-slate-600/60 text-slate-300 hover:text-white border-slate-600/30`;

  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Paperclip className="w-3.5 h-3.5 text-amber-500/50 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-xs text-slate-200 truncate">{displayName}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[11px] text-slate-600">{typeLabel}</span>
            {size     && <span className="text-[11px] text-slate-700">· {size}</span>}
            {isSystem && <span className="text-[11px] text-slate-700 italic">· System</span>}
          </div>
        </div>
      </div>
      <button
        onClick={async () => {
          try {
            await navigateToAttachmentViewer({ referralId, attachmentId });
          } catch {
            // Session creation failed — fail silently, user can retry
          }
        }}
        className={btnNormal}
        title="View document"
      >
        <Eye className="w-3 h-3" /> View
      </button>
    </div>
  );
}

// ── Consult Document Upload ───────────────────────────────────────────────────
// Specialist / specialist_admin upload panel — shown when workflow status = SEEN.
// Uses the signed-URL PHI attachment flow: createUploadUrl → PUT → finalize.

const CONSULT_DOC_ACCEPT = [
  'application/pdf',
  'image/jpeg', 'image/png', 'image/webp', 'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
].join(',');

const CONSULT_DOC_MAX_SIZE = 25 * 1024 * 1024; // 25 MB matches server-side PDF limit

function ConsultDocumentUpload({ referralId, onUploaded, activeFocusTarget }) {
  const [file,     setFile]     = useState(null);
  const [phase,    setPhase]    = useState('idle');   // idle | uploading | finalizing | done | error
  const [progress, setProgress] = useState(0);
  const [errMsg,   setErrMsg]   = useState(null);

  const reset = () => {
    setFile(null);
    setPhase('idle');
    setProgress(0);
    setErrMsg(null);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.size > CONSULT_DOC_MAX_SIZE) {
      setErrMsg(`File exceeds 25 MB limit (${fmtBytes(selected.size)}).`);
      return;
    }
    setFile(selected);
    setErrMsg(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setPhase('uploading');
    setProgress(0);
    setErrMsg(null);

    try {
      const { attachmentId, uploadUrl } = await createAttachmentUploadUrl(referralId, {
        mimeType:     file.type,
        originalName: file.name,
        type:         'CONSULT_DOCUMENT',
        category:     'CONSULT_DOCUMENT',
        purpose:      'CLOSED_LOOP_EVIDENCE',
      });

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload  = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (HTTP ${xhr.status})`));
        xhr.onerror = () => reject(new Error('Network error during upload.'));
        xhr.send(file);
      });

      setPhase('finalizing');
      await finalizeAttachmentUpload(referralId, attachmentId);

      setPhase('done');
      if (onUploaded) onUploaded();
    } catch (err) {
      setPhase('error');
      setErrMsg(err.message ?? 'Upload failed. Please retry.');
    }
  };

  const isDone  = phase === 'done';
  const isBusy  = phase === 'uploading' || phase === 'finalizing';
  const isError = phase === 'error';

  return (
    <div data-learning-target="specialist-consult-upload" className={`mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4${activeFocusTarget === 'specialist-consult-upload' ? ' ring-2 ring-emerald-400/20' : ''}`}>
      <div className="flex items-center gap-2 mb-3">
        <Upload className="w-4 h-4 text-emerald-400" />
        <span className="text-xs font-semibold text-emerald-300">Upload Consult Document</span>
      </div>

      {isDone ? (
        <div className="flex items-center gap-2 py-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span className="text-xs text-emerald-300">Consult document uploaded and verified.</span>
        </div>
      ) : (
        <>
          {!file ? (
            <label className="flex flex-col items-center justify-center gap-2 py-5 rounded-lg border-2 border-dashed border-slate-600/50 hover:border-emerald-500/40 cursor-pointer transition-colors">
              <Paperclip className="w-5 h-5 text-slate-500" />
              <span className="text-xs text-slate-400">Click to select a file</span>
              <span className="text-[11px] text-slate-600">PDF, Word, or image · 25 MB max</span>
              <input type="file" accept={CONSULT_DOC_ACCEPT} onChange={handleFileChange} className="hidden" />
            </label>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2.5">
                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-slate-200 truncate">{file.name}</p>
                  <p className="text-[11px] text-slate-600">{fmtBytes(file.size)}</p>
                </div>
                {!isBusy && (
                  <button onClick={reset} className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {isBusy && (
                <div className="space-y-1.5">
                  <div className="w-full h-1.5 rounded-full bg-slate-700 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                      style={{ width: `${phase === 'finalizing' ? 100 : progress}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-slate-500">
                    {phase === 'finalizing' ? 'Verifying upload…' : `Uploading… ${progress}%`}
                  </p>
                </div>
              )}

              {!isBusy && (
                <button
                  onClick={handleUpload}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" /> Upload Document
                </button>
              )}
            </div>
          )}

          {isError && errMsg && (
            <div className="flex items-start gap-2 mt-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-300">{errMsg}</p>
            </div>
          )}
          {!isError && errMsg && (
            <p className="text-xs text-red-400 mt-2">{errMsg}</p>
          )}
        </>
      )}
    </div>
  );
}

// ── Consult Detail Drawer ──────────────────────────────────────────────────────

function ConsultDetailDrawer({ item, uid, role, onClose, onCompleted, readOnly, activeFocusTarget }) {
  const { referral, workflow } = item;
  const tenantId   = referral?.tenantId;
  const referralId = referral?.id;

  // ── Sub-data ────────────────────────────────────────────────────────────────
  const [events,     setEvents]     = useState([]);
  const [rework,     setRework]     = useState([]);
  const [userNames,  setUserNames]  = useState({});
  const [phi,        setPhi]        = useState(null);
  const [phiError,   setPhiError]   = useState(false);
  const [subLoading, setSubLoading] = useState(true);

  const loadSubData = useCallback(() => {
    if (!tenantId || !referralId) { setSubLoading(false); return; }
    setSubLoading(true);
    setPhiError(false);

    Promise.all([
      listWorkflowEvents(tenantId, referralId).catch((e) => { console.warn('[Drawer] events:', e?.message); return []; }),
      listReworkRequests(tenantId, referralId).catch((e) => { console.warn('[Drawer] rework:', e?.message); return []; }),
      getPatientIdentity(referralId).catch((e) => { console.warn('[Drawer] identity:', e?.message); return null; }),
      getContact(referralId).catch((e)         => { console.warn('[Drawer] contact:', e?.message);  return null; }),
      getInsurance(referralId).catch((e)        => { console.warn('[Drawer] insurance:', e?.message);return null; }),
      listAttachments(referralId).catch((e)     => { console.warn('[Drawer] attachments:', e?.message);return null; }),
    ]).then(([evts, rwk, identity, contact, insurance, attachResult]) => {
      setEvents(evts ?? []);
      setRework(rwk ?? []);

      const phiOk = identity !== null || contact !== null || insurance !== null || attachResult !== null;
      if (phiOk) {
        setPhi({
          identity:    identity?.data    ?? null,
          contact:     contact?.data     ?? null,
          insurance:   insurance?.data   ?? null,
          attachments: attachResult?.attachments ?? [],
        });
      } else {
        setPhiError(true);
        setPhi({ identity: null, contact: null, insurance: null, attachments: [] });
      }

      const uids = new Set();
      const addUid = (u) => { if (u && typeof u === 'string') uids.add(u); };
      addUid(referral?.createdByUid);
      addUid(workflow?.ownerUid);
      addUid(workflow?.scheduling?.scheduledByUid);
      addUid(workflow?.scheduling?.scheduledBy);
      addUid(workflow?.closedLoop?.closedByUid);
      (evts ?? []).forEach((e) => addUid(e.actorUid));

      if (uids.size === 0) { setSubLoading(false); return; }

      Promise.all(
        [...uids].map((u) =>
          getUser(u).then((doc) => (doc?.name ? [u, doc.name] : null)).catch(() => null)
        )
      ).then((results) => {
        const map = {};
        results.forEach((entry) => { if (entry) map[entry[0]] = entry[1]; });
        setUserNames(map);
        setSubLoading(false);
      });
    });
  }, [tenantId, referralId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadSubData(); }, [loadSubData]);

  useEffect(() => {
    if (!activeFocusTarget) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(timer);
  }, [activeFocusTarget]);

  const displayName = useCallback(
    (u) => (u ? (userNames[u] ?? u) : '—'),
    [userNames],
  );

  // ── Derived values ──────────────────────────────────────────────────────────
  const finalOutcome        = referral?.finalOutcome ?? referral?.outcome?.status ?? null;
  const outcomeReason       = referral?.outcome?.reason ?? null;
  const outcomeCfg          = finalOutcome ? (OUTCOME_CONFIG[finalOutcome] ?? null) : null;
  const triggeredRedFlagIds = Array.isArray(referral?.triggeredRedFlagIds) ? referral.triggeredRedFlagIds : [];
  const missingFieldIds     = Array.isArray(referral?.missingFieldIds)     ? referral.missingFieldIds     : [];
  const receiptToken        = referral?.receiptToken ?? null;

  const workflowStatus    = workflow?.status ?? null;
  const statusCfg         = workflowStatus ? (STATUS_CONFIG[workflowStatus] ?? null) : null;
  const priority          = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;
  const priorityCfg       = PRIORITY_CONFIG[priority];
  const ownerUid          = workflow?.ownerUid ?? null;
  const missingItemsCount = workflow?.rework?.missingItemsCount ?? 0;

  const apptDate       = workflow?.scheduling?.appointmentDate ?? null;
  const apptTime       = workflow?.scheduling?.appointmentTime ?? null;
  const consultantName = workflow?.scheduling?.consultantName ?? workflow?.scheduling?.consultantId ?? null;
  const locationId     = workflow?.scheduling?.locationId ?? null;
  const schedulingNote = workflow?.scheduling?.schedulingNote ?? null;
  const scheduledAt    = workflow?.scheduling?.scheduledAt ?? null;
  const scheduledByUid = workflow?.scheduling?.scheduledByUid ?? workflow?.scheduling?.scheduledBy ?? null;
  const hasAppt        = apptDate || scheduledAt;

  const visitType           = referral?.visitType ?? referral?.patient?.visitType ?? null;
  const reasonForEscalation = referral?.reasonForEscalation ?? referral?.patient?.reasonForEscalation ?? null;
  const visitPurpose        = referral?.visitPurpose ?? referral?.patient?.visitPurpose ?? null;
  const localId             = referral?.localId ?? null;
  const hasVisitInfo        = visitType || reasonForEscalation || visitPurpose;

  const rawAnswers    = referral?.clinicalAnswers ?? referral?.answers ?? null;
  const answerEntries = rawAnswers && typeof rawAnswers === 'object'
    ? Object.entries(rawAnswers).filter(([, v]) => v !== null && v !== undefined && v !== '')
    : [];

  const triageDecision   = workflow?.triage?.triageDecision ?? null;
  const triageNote       = workflow?.triageNote ?? null;
  const overrideDecision = workflow?.overrideDecision ?? null;
  const overrideReason   = workflow?.overrideReason ?? null;
  const overrideNote     = workflow?.overrideNote ?? null;
  const hasTriageData    = (triageDecision && triageDecision !== 'none')
    || triageNote || overrideDecision || overrideReason || overrideNote;

  const closedAt       = workflow?.closedLoop?.closedAt ?? null;
  const closedByUid    = workflow?.closedLoop?.closedByUid ?? null;
  const closedLoopNote = workflow?.closedLoop?.closedLoopNote ?? null;
  const [hasConsultDoc, setHasConsultDoc] = useState(workflow?.closedLoop?.hasConsultDocument === true);

  const handleConsultDocUploaded = useCallback(() => {
    setHasConsultDoc(true);
    loadSubData();
  }, [loadSubData]);

  const patientName  = phi ? fmtPatientName(phi.identity?.name) : null;
  const patientDob   = phi ? fmtDob(phi.identity?.dob) : null;
  const patientSex   = phi?.identity?.sex ?? null;
  const identifiers  = phi?.identity?.identifiers ?? [];
  const phone        = phi?.contact?.phone ?? null;
  const emailAddr    = phi?.contact?.email ?? null;
  const address      = phi ? fmtAddress(phi.contact?.address) : null;
  const hasContact   = phone || emailAddr || address;
  const payerName    = phi?.insurance?.payerName   ?? null;
  const payerId      = phi?.insurance?.payerId     ?? null;
  const memberId     = phi?.insurance?.memberId    ?? null;
  const groupNumber  = phi?.insurance?.groupNumber ?? null;
  const planType     = phi?.insurance?.planType    ?? null;
  const hasInsurance = payerName || payerId || memberId || groupNumber || planType;
  const attachments  = phi?.attachments ?? [];

  // ── Complete consult ────────────────────────────────────────────────────────
  const [note,       setNote]       = useState('');
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState(null);
  const [marking,    setMarking]    = useState(false);
  const [markError,  setMarkError]  = useState(null);

  const isScheduled = workflowStatus === WORKFLOW_STATUS.SCHEDULED;
  const isSeen      = workflowStatus === WORKFLOW_STATUS.SEEN;

  const apptDateRaw  = workflow?.scheduling?.appointmentDate ?? null;
  const isUnreconciled = isScheduled && (() => {
    if (!apptDateRaw) return false;
    const str = typeof apptDateRaw === 'string' ? apptDateRaw : null;
    if (!str) return false;
    return str.slice(0, 10) < TODAY_STR;
  })();

  const [noShowBusy, setNoShowBusy] = useState(false);
  const [noShowError, setNoShowError] = useState(null);
  const [noShowConfirmed, setNoShowConfirmed] = useState(false);
  const [showNoShowConfirm, setShowNoShowConfirm] = useState(false);

  const appointmentHasPassed = useMemo(() => {
    if (!apptDateRaw) return false;
    const dateStr = typeof apptDateRaw === 'string' ? apptDateRaw : null;
    if (!dateStr) return false;
    const timeStr = workflow?.scheduling?.appointmentTime ?? '23:59';
    const apptTime = new Date(`${dateStr}T${timeStr}:00`);
    return Date.now() >= apptTime.getTime();
  }, [apptDateRaw, workflow?.scheduling?.appointmentTime]);

  // "Close Referral Loop" — works from both SCHEDULED (atomic) and SEEN (one step).
  const handleComplete = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      if (isScheduled) {
        // SCHEDULED → SEEN → CLOSED_LOOP atomically
        await completeConsult(
          tenantId, referralId,
          { closedLoopNote: note.trim() || null },
          uid, role,
        );
      } else {
        // SEEN → CLOSED_LOOP (specialist reached SEEN via markAsSeen)
        await closeLoop(
          tenantId, referralId,
          { closedLoopNote: note.trim() || '' },
          uid, role,
        );
      }
      onCompleted(referralId);
      onClose();
    } catch (err) {
      console.error('[ConsultDetailDrawer] handleComplete failed:', err);
      setSaveError(safeUserError(err) ?? 'Failed to complete consult. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  // "Mark as Seen" — SCHEDULED → SEEN (single step, keeps drawer open).
  const handleMarkSeen = async () => {
    setMarkError(null);
    setMarking(true);
    try {
      const opts = isUnreconciled ? { trigger: 'SPECIALIST_RECONCILIATION' } : undefined;
      await markAsSeen(tenantId, referralId, uid, role, opts);
      onCompleted(referralId, /* keepOpen */ true);
    } catch (err) {
      console.error('[ConsultDetailDrawer] markAsSeen failed:', err);
      setMarkError(safeUserError(err) ?? 'Failed to mark as seen. Please retry.');
    } finally {
      setMarking(false);
    }
  };

  // "Patient Did Not Show" — SCHEDULED → NO_SHOW (removes from worklist).
  const handleMarkNoShow = async () => {
    setNoShowError(null);
    setNoShowBusy(true);
    try {
      const opts = isUnreconciled ? { trigger: 'SPECIALIST_RECONCILIATION' } : undefined;
      await markAsNoShow(tenantId, referralId, uid, role, opts);
      onCompleted(referralId);
      onClose();
    } catch (err) {
      console.error('[ConsultDetailDrawer] markAsNoShow failed:', err);
      setNoShowError(safeUserError(err) ?? 'Failed to mark as no-show. Please retry.');
    } finally {
      setNoShowBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={() => !saving && onClose()} aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl flex flex-col bg-slate-900 border-l border-slate-700/60 shadow-2xl">

        {/* Header */}
        <div data-learning-target="referral-summary-header" className={`flex items-start justify-between px-6 py-5 border-b border-slate-800/60 flex-shrink-0 bg-slate-900/95${activeFocusTarget === 'referral-summary-header' ? ' ring-2 ring-emerald-400/20 rounded-b-xl' : ''}`}>
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[11px] text-slate-500 font-mono">{referralId ?? '—'}</span>
              {receiptToken && <><span className="text-slate-700">·</span><span className="text-[11px] text-slate-500 font-mono">{receiptToken}</span></>}
            </div>
            <h2 className="text-base font-semibold text-white leading-tight">
              {fmtLabel(referral?.specialtyId)}
              {referral?.packId && <span className="text-slate-400 font-normal"> · {fmtLabel(referral.packId)}</span>}
            </h2>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {outcomeCfg  && <Badge tw={outcomeCfg.tw}  label={outcomeCfg.label} />}
              {priorityCfg && <Badge tw={priorityCfg.tw} label={priorityCfg.label} />}
              {statusCfg   && <Badge tw={statusCfg.tw}   label={statusCfg.label} />}
              {readOnly    && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 font-medium"><CheckCircle2 className="w-3 h-3" /> Closed</span>}
            </div>
          </div>
          <button onClick={() => !saving && onClose()} disabled={saving}
            className="p-2 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-colors flex-shrink-0 disabled:opacity-50" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Patient Information (PHI) + Contact + Insurance */}
          <div data-learning-target="patient-contact-insurance" className={`space-y-6${activeFocusTarget === 'patient-contact-insurance' ? ' ring-2 ring-emerald-400/20 rounded-xl p-1 -m-1 transition-all duration-300' : ''}`}>
          <div>
            <SectionHeading icon={User} label="Patient Information" phi />
            {subLoading ? (
              <div className="flex items-center gap-2 py-3"><Loader2 className="w-3.5 h-3.5 text-amber-500/50 animate-spin" /><span className="text-xs text-slate-600">Loading patient info…</span></div>
            ) : phiError ? <PhiLoadError onRetry={loadSubData} />
            : !patientName && !patientDob ? <EmptyState message="No patient identity on file." />
            : (
              <SectionCard phi>
                {patientName && <DetailRow label="Full Name"     value={patientName} />}
                {patientDob  && <DetailRow label="Date of Birth" value={patientDob} />}
                {patientSex  && <DetailRow label="Sex"           value={fmtLabel(patientSex)} />}
                {identifiers.map((id, i) => (
                  <DetailRow key={i} label={fmtLabel(id.type ?? id.system ?? `Identifier ${i + 1}`)} value={id.value ?? '—'} mono />
                ))}
              </SectionCard>
            )}
          </div>

          {/* Contact (PHI) */}
          <div>
            <SectionHeading icon={Phone} label="Contact" phi />
            {subLoading ? (
              <div className="flex items-center gap-2 py-3"><Loader2 className="w-3.5 h-3.5 text-amber-500/50 animate-spin" /><span className="text-xs text-slate-600">Loading contact…</span></div>
            ) : phiError ? <PhiLoadError onRetry={loadSubData} />
            : !hasContact ? <EmptyState message="No contact information on file." />
            : (
              <SectionCard phi>
                {phone    && <DetailRow label="Phone"   value={phone} />}
                {emailAddr && <DetailRow label="Email"   value={emailAddr} />}
                {address  && <DetailRow label="Address" value={address} />}
              </SectionCard>
            )}
          </div>

          {/* Insurance (PHI) */}
          <div>
            <SectionHeading icon={CreditCard} label="Insurance" phi />
            {subLoading ? (
              <div className="flex items-center gap-2 py-3"><Loader2 className="w-3.5 h-3.5 text-amber-500/50 animate-spin" /><span className="text-xs text-slate-600">Loading insurance…</span></div>
            ) : phiError ? <PhiLoadError onRetry={loadSubData} />
            : !hasInsurance ? <EmptyState message="No insurance information on file." />
            : (
              <SectionCard phi>
                {payerName   && <DetailRow label="Payer"        value={payerName} />}
                {payerId     && <DetailRow label="Payer ID"     value={payerId} mono />}
                {memberId    && <DetailRow label="Member ID"    value={memberId} mono />}
                {groupNumber && <DetailRow label="Group Number" value={groupNumber} mono />}
                {planType    && <DetailRow label="Plan Type"    value={fmtLabel(planType)} />}
              </SectionCard>
            )}
          </div>
          </div>

          {/* Attachments (PHI) */}
          <div data-learning-target="attachments" className={activeFocusTarget === 'attachments' ? 'ring-2 ring-emerald-400/20 rounded-xl p-1 -m-1 transition-all duration-300' : ''}>
            <SectionHeading icon={Paperclip} label="Attachments" phi
              count={!subLoading && attachments.length > 0 ? `${attachments.length}` : null} />
            {subLoading ? (
              <div className="flex items-center gap-2 py-3"><Loader2 className="w-3.5 h-3.5 text-amber-500/50 animate-spin" /><span className="text-xs text-slate-600">Loading attachments…</span></div>
            ) : phiError ? <PhiLoadError onRetry={loadSubData} />
            : attachments.length === 0 ? <EmptyState message="No attachments on file for this referral." />
            : (
              <div className="bg-amber-500/5 rounded-xl border border-amber-500/20 px-4 divide-y divide-amber-500/10">
                {attachments.map((att, i) => (
                  <AttachmentRow key={att.attachmentId ?? att.id ?? i} tenantId={tenantId} referralId={referralId} attachment={att} />
                ))}
              </div>
            )}
          </div>

          {/* Consult Document Upload — specialist/specialist_admin only, SCHEDULED or SEEN status */}
          {(isScheduled || isSeen) && !readOnly && (role === 'specialist' || role === 'specialist_admin') && (
            <div data-learning-target="upload-consult-document" className={activeFocusTarget === 'upload-consult-document' ? 'ring-2 ring-emerald-400/20 rounded-xl p-1 -m-1 transition-all duration-300' : ''}>
              <ConsultDocumentUpload referralId={referralId} onUploaded={handleConsultDocUploaded} activeFocusTarget={activeFocusTarget} />
            </div>
          )}

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-800/60" />
            <span className="text-[10px] text-slate-700 uppercase tracking-widest font-medium">Clinical Data</span>
            <div className="flex-1 h-px bg-slate-800/60" />
          </div>

          {/* Gate Decision */}
          <div data-learning-target="gate-decision" className={activeFocusTarget === 'gate-decision' ? 'ring-2 ring-emerald-400/20 rounded-xl p-1 -m-1 transition-all duration-300' : ''}>
            <SectionHeading icon={Shield} label="Gate Decision" />
            <SectionCard>
              <DetailRow label="Gate Outcome"  value={outcomeCfg?.label ?? fmtLabel(finalOutcome)} />
              {outcomeReason && <DetailRow label="Outcome Reason" value={outcomeReason} />}
              {triggeredRedFlagIds.length > 0 && <DetailRow label={`Red Flags (${triggeredRedFlagIds.length})`} value={triggeredRedFlagIds.map(fmtLabel).join(', ')} />}
              {missingFieldIds.length > 0     && <DetailRow label={`Missing at Submit (${missingFieldIds.length})`} value={missingFieldIds.map(fmtLabel).join(', ')} dim />}
              {receiptToken && <DetailRow label="EHR Token" value={receiptToken} mono />}
            </SectionCard>
          </div>

          {/* Referral Details + Visit Information */}
          <div data-learning-target="referral-visit-info" className={`space-y-6${activeFocusTarget === 'referral-visit-info' ? ' ring-2 ring-emerald-400/20 rounded-xl p-1 -m-1 transition-all duration-300' : ''}`}>
          <div>
            <SectionHeading icon={Hash} label="Referral Details" />
            <SectionCard>
              <DetailRow label="Specialty"       value={fmtLabel(referral?.specialtyId)} />
              <DetailRow label="Pack"            value={fmtLabel(referral?.packId)} />
              {referral?.versionId && <DetailRow label="Pack Version"  value={referral.versionId} mono />}
              <DetailRow label="Referral ID"     value={referralId} mono />
              <DetailRow label="Submitted"       value={fmtTs(referral?.createdAt)} />
              {referral?.createdByUid && <DetailRow label="Submitted By"  value={displayName(referral.createdByUid)} />}
              <DetailRow label="Workflow Status" value={statusCfg?.label ?? fmtLabel(workflowStatus)} />
              {workflow?.statusUpdatedAt && <DetailRow label="Status Since" value={fmtTs(workflow.statusUpdatedAt)} />}
              {ownerUid          && <DetailRow label="Assigned To"   value={displayName(ownerUid)} />}
              {missingItemsCount > 0 && <DetailRow label="Missing Items" value={String(missingItemsCount)} />}
            </SectionCard>
          </div>

          {/* Visit Information */}
          {(hasVisitInfo || localId) && (
            <div>
              <SectionHeading icon={FileText} label="Visit Information" />
              <SectionCard>
                {visitType && <DetailRow label="Visit Type" value={VISIT_TYPE_LABELS[visitType] ?? fmtLabel(visitType)} />}
                {reasonForEscalation && (
                  <DetailRow label="Reason for Escalation"
                    value={Array.isArray(reasonForEscalation) ? reasonForEscalation.map(fmtLabel).join(', ') : fmtLabel(reasonForEscalation)} />
                )}
                {visitPurpose && <DetailRow label="Visit Purpose" value={VISIT_PURPOSE_LABELS[visitPurpose] ?? fmtLabel(visitPurpose)} />}
                {localId      && <DetailRow label="Local Reference" value={localId} mono />}
              </SectionCard>
            </div>
          )}
          </div>

          {/* Clinical Answers */}
          <div data-learning-target="clinical-answers" className={activeFocusTarget === 'clinical-answers' ? 'ring-2 ring-emerald-400/20 rounded-xl p-1 -m-1 transition-all duration-300' : ''}>
            <SectionHeading icon={ClipboardList} label="Clinical Answers"
              count={answerEntries.length > 0 ? `${answerEntries.length} fields` : null} />
            {answerEntries.length > 0 ? (
              <SectionCard>
                {answerEntries.map(([key, val]) => (
                  <DetailRow key={key} label={fmtLabel(key)} value={fmtAnswerValue(val)} />
                ))}
              </SectionCard>
            ) : (
              <EmptyState message="No clinical answers recorded for this referral." />
            )}
          </div>

          {/* Appointment + Triage */}
          <div data-learning-target="appointment-triage" className={`space-y-6${activeFocusTarget === 'appointment-triage' ? ' ring-2 ring-emerald-400/20 rounded-xl p-1 -m-1 transition-all duration-300' : ''}`}>
          {hasAppt && (
            <div>
              <SectionHeading icon={Calendar} label="Appointment" />
              <SectionCard>
                {apptDate ? (
                  <DetailRow label="Date & Time"
                    value={`${new Date(apptDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${apptTime ? ' · ' + (from24h(apptTime) ?? apptTime) : ''}`} />
                ) : (
                  scheduledAt && <DetailRow label="Booked At" value={fmtTs(scheduledAt)} />
                )}
                {consultantName && <DetailRow label="Specialist"      value={consultantName} />}
                {locationId     && <DetailRow label="Location"        value={locationId} />}
                {schedulingNote && <DetailRow label="Scheduling Note" value={schedulingNote} />}
                {scheduledAt && apptDate && <DetailRow label="Booked At" value={fmtTs(scheduledAt)} />}
                {scheduledByUid && <DetailRow label="Booked By" value={displayName(scheduledByUid)} />}
              </SectionCard>
            </div>
          )}

          {/* Triage Annotations */}
          {hasTriageData && (
            <div>
              <SectionHeading icon={Stethoscope} label="Triage Annotations" />
              <SectionCard>
                {triageDecision && triageDecision !== 'none' && <DetailRow label="Triage Decision" value={TRIAGE_DECISION_LABELS[triageDecision] ?? fmtLabel(triageDecision)} />}
                {triageNote      && <DetailRow label="Triage Note"       value={triageNote} />}
                {overrideDecision && <DetailRow label="Override Decision" value={fmtLabel(overrideDecision)} />}
                {overrideReason  && <DetailRow label="Override Reason"   value={overrideReason} />}
                {overrideNote    && <DetailRow label="Override Note"     value={overrideNote} />}
              </SectionCard>
            </div>
          )}
          </div>

          {/* Rework Items + Workflow History */}
          <div data-learning-target="rework-workflow-history" className={`space-y-6${activeFocusTarget === 'rework-workflow-history' ? ' ring-2 ring-emerald-400/20 rounded-xl p-1 -m-1 transition-all duration-300' : ''}`}>
          <div>
            <SectionHeading icon={RotateCcw} label="Rework Items"
              count={!subLoading && rework.length > 0 ? `${rework.length}` : null} />
            {subLoading ? (
              <div className="flex items-center gap-2 py-3"><Loader2 className="w-3.5 h-3.5 text-slate-500 animate-spin" /><span className="text-xs text-slate-600">Loading…</span></div>
            ) : rework.length === 0 ? <EmptyState message="No rework requests for this referral." />
            : (
              <div className="space-y-1.5">
                {rework.map((req) => {
                  const sCfg      = REWORK_STATUS_CONFIG[req.status] ?? REWORK_STATUS_CONFIG.open;
                  const typeLabel = REWORK_ITEM_TYPE_LABELS[req.itemType] ?? fmtLabel(req.itemType ?? 'Unknown');
                  return (
                    <div key={req.id ?? req.requestId} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-slate-800/40 border border-slate-700/30">
                      <span className="text-xs text-slate-200 font-medium">{typeLabel}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-slate-600">{fmtDate(req.createdAt)}</span>
                        <Badge tw={sCfg.tw} label={sCfg.label} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Workflow History */}
          <div>
            <SectionHeading icon={GitBranch} label="Workflow History"
              count={!subLoading && events.length > 0 ? `${events.length} events` : null} />
            {subLoading ? (
              <div className="flex items-center gap-2 py-3"><Loader2 className="w-3.5 h-3.5 text-slate-500 animate-spin" /><span className="text-xs text-slate-600">Loading…</span></div>
            ) : events.length === 0 ? <EmptyState message="No workflow events recorded." />
            : (
              <div className="relative pl-4">
                <div className="absolute left-4 top-2 bottom-2 w-px bg-slate-800" />
                {events.map((evt, idx) => {
                  const cfg  = EVENT_CONFIG[evt.eventType] ?? { label: fmtLabel(evt.eventType ?? 'Event'), icon: Activity, color: 'text-slate-400' };
                  const Icon = cfg.icon;
                  return (
                    <div key={evt.id ?? idx} className="relative flex items-start gap-3 py-2.5">
                      <div className="relative z-10 w-5 h-5 rounded-full bg-slate-800 border border-slate-700/60 flex items-center justify-center flex-shrink-0 -ml-2.5">
                        <Icon className={`w-2.5 h-2.5 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <p className="text-xs font-medium text-slate-200">
                          {cfg.label}
                          {evt.fromStatus && evt.toStatus && (
                            <span className="text-slate-500 font-normal"> · {STATUS_CONFIG[evt.fromStatus]?.label ?? evt.fromStatus} → {STATUS_CONFIG[evt.toStatus]?.label ?? evt.toStatus}</span>
                          )}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          <span className="text-[11px] text-slate-600">{fmtTs(evt.createdAt)}</span>
                          {evt.actorUid && (
                            <span className="text-[11px] text-slate-500">
                              · {displayName(evt.actorUid)}
                              {evt.actorRole && <span className="text-slate-600"> ({ROLE_DISPLAY[evt.actorRole]?.name ?? fmtLabel(evt.actorRole)})</span>}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </div>

          {/* Consult Completed (readOnly) */}
          {readOnly && closedAt && (
            <div>
              <SectionHeading icon={CheckCircle2} label="Consult Completed" />
              <div className="bg-emerald-500/5 rounded-xl border border-emerald-500/20 px-4 py-3 divide-y divide-emerald-500/10">
                <DetailRow label="Closed At"    value={fmtTs(closedAt)} />
                {closedByUid  && <DetailRow label="Closed By"    value={displayName(closedByUid)} />}
                {closedLoopNote
                  ? <DetailRow label="Consult Note" value={closedLoopNote} />
                  : <DetailRow label="Consult Note" value="No note recorded." dim />}
              </div>
            </div>
          )}

          <div className="h-4" />
        </div>

        {/* Sticky footer — active referrals only.
            specialist       : shows for SCHEDULED (Mark as Seen + Close) and SEEN (Close only).
            specialist_admin : shows for SEEN only (Close only); cannot transition SCHEDULED→SEEN. */}
        {!readOnly && (isScheduled || isSeen) && !(role === 'specialist_admin' && isScheduled) && (
          <div data-learning-target="consult-note-actions" className={`flex-shrink-0 border-t border-slate-800/60 px-6 py-5 bg-slate-900/95 backdrop-blur-sm${activeFocusTarget === 'consult-note-actions' ? ' ring-2 ring-emerald-400/20 rounded-xl transition-all duration-300' : ''}`}>
            <div data-learning-target="specialist-consult-note" className={activeFocusTarget === 'specialist-consult-note' ? 'ring-2 ring-emerald-400/20 rounded-xl mb-3' : 'mb-3'}>
              <label className="block text-xs font-medium text-slate-400 mb-2">
                Consult Note <span className="text-slate-600 font-normal">(optional · non-PHI only)</span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={saving || marking || noShowBusy}
                rows={2}
                placeholder="e.g. Reviewed, follow-up in 3 months…"
                className="w-full px-3 py-2 rounded-xl bg-slate-800/60 border border-slate-700/50 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/40 resize-none disabled:opacity-50 transition-colors"
              />
            </div>
            {isSeen && !note.trim() && !hasConsultDoc && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-300">
                  A consult note or an uploaded consult document is required to close the referral loop.
                </p>
              </div>
            )}
            {(markError || saveError || noShowError) && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-3">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-300">{markError || saveError || noShowError}</p>
              </div>
            )}
            <div data-learning-target="specialist-unreconciled-actions" className={`flex items-center gap-3${activeFocusTarget === 'specialist-unreconciled-actions' ? ' learning-focus-highlight-subtle rounded-xl p-1 -m-1 transition-all duration-300' : ''}`}>
              {isScheduled && role !== 'specialist_admin' && (
                <button
                  data-learning-target="specialist-mark-seen-btn"
                  type="button"
                  onClick={handleMarkSeen}
                  disabled={marking || saving || noShowBusy}
                  className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white text-sm font-semibold transition-colors${activeFocusTarget === 'specialist-mark-seen-btn' ? ' ring-2 ring-blue-400/30' : ''}`}
                >
                  {marking
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Marking seen…</>
                    : <><Eye className="w-4 h-4" /> Mark as Seen</>}
                </button>
              )}
              <button
                data-learning-target="specialist-close-loop-btn"
                type="button"
                onClick={handleComplete}
                disabled={saving || marking || noShowBusy || (isSeen && !note.trim() && !hasConsultDoc)}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold transition-colors${activeFocusTarget === 'specialist-close-loop-btn' ? ' ring-2 ring-emerald-400/20' : ''}`}
              >
                {saving
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Closing loop…</>
                  : <><CheckCircle2 className="w-5 h-5" /> Close Referral Loop</>}
              </button>
              {isScheduled && role !== 'specialist_admin' && !showNoShowConfirm && (
                <button
                  data-learning-target="specialist-no-show-btn"
                  type="button"
                  onClick={() => { setShowNoShowConfirm(true); setNoShowConfirmed(false); }}
                  disabled={noShowBusy || saving || marking}
                  className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl bg-red-600/80 hover:bg-red-500/80 disabled:opacity-50 text-white text-sm font-semibold transition-colors${activeFocusTarget === 'specialist-no-show-btn' ? ' ring-2 ring-red-400/30' : ''}`}
                >
                  <UserX className="w-4 h-4" /> Patient Did Not Show
                </button>
              )}
            </div>
            {isScheduled && showNoShowConfirm && (
              <div className="mt-3 p-3 rounded-xl bg-orange-500/5 border border-orange-500/15">
                <p className="text-xs text-orange-300/80 mb-3">
                  This will permanently mark the referral as <strong>No-Show</strong>.
                  The patient did not attend the scheduled appointment.
                  This action is terminal and cannot be undone.
                </p>
                {apptDateRaw && (
                  <p className="text-xs text-slate-400 mb-3">
                    Appointment date: <span className="text-slate-200 font-medium">{typeof apptDateRaw === 'string' ? apptDateRaw : '—'}</span>
                    {!appointmentHasPassed && (
                      <span className="ml-2 text-amber-400 font-medium">· Appointment has not passed yet</span>
                    )}
                  </p>
                )}
                <label className="flex items-start gap-2.5 cursor-pointer group mb-3">
                  <input
                    type="checkbox"
                    checked={noShowConfirmed}
                    onChange={(e) => setNoShowConfirmed(e.target.checked)}
                    className="mt-0.5 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500/30"
                  />
                  <span className="text-xs text-slate-300 group-hover:text-white transition-colors">
                    I confirm that the appointment time has passed and the patient did not attend.
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleMarkNoShow}
                    disabled={!noShowConfirmed || !appointmentHasPassed || noShowBusy || saving || marking}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-600/80 hover:bg-red-500/80 disabled:opacity-50 text-white text-xs font-semibold transition-colors"
                  >
                    {noShowBusy
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Marking…</>
                      : <><UserX className="w-3.5 h-3.5" /> Confirm No-Show</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowNoShowConfirm(false); setNoShowConfirmed(false); }}
                    disabled={noShowBusy}
                    className="px-4 py-2.5 rounded-xl bg-slate-700/60 hover:bg-slate-600/60 text-slate-300 text-xs font-medium transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ── Referral Card ─────────────────────────────────────────────────────────────
// Shared by Today, Needs Action, and Upcoming tabs.
// Shows an overdue warning pill on Needs Action items.

function ReferralCard({ item, onClick, overdue = false, seen = false, highlightFirst = false }) {
  const { referral, workflow } = item;
  const priority       = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;
  const apptDate       = workflow?.scheduling?.appointmentDate ?? null;
  const apptTime       = workflow?.scheduling?.appointmentTime ?? null;
  const consultantName = workflow?.scheduling?.consultantName ?? workflow?.scheduling?.consultantId ?? null;
  const finalOutcome   = referral?.finalOutcome ?? referral?.outcome?.status ?? null;
  const outcomeCfg     = finalOutcome ? (OUTCOME_CONFIG[finalOutcome] ?? null) : null;
  const isUrgent       = priority === WORKFLOW_PRIORITY.URGENT;
  const isHigh         = priority === WORKFLOW_PRIORITY.HIGH;

  return (
    <button
      onClick={onClick}
      {...(highlightFirst ? { 'data-learning-target': 'specialist-referral-card' } : {})}
      className={`w-full text-left flex items-start gap-4 px-5 py-4 rounded-xl border transition-all group
        ${overdue
          ? 'bg-amber-500/5 border-amber-500/20 hover:bg-amber-500/10 hover:border-amber-500/40'
          : isUrgent
          ? 'bg-red-500/5 border-red-500/20 hover:bg-red-500/10'
          : isHigh
          ? 'bg-orange-500/5 border-orange-500/20 hover:bg-orange-500/10'
          : 'bg-slate-900/60 border-slate-700/50 hover:bg-slate-800/50 hover:border-slate-600/60'}
        ${highlightFirst ? ' ring-2 ring-teal-400/20' : ''}`}
    >
      {/* Icon */}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 border
        ${overdue
          ? 'bg-amber-500/10 border-amber-500/20'
          : 'bg-teal-500/10 border-teal-500/20'}`}>
        <Calendar className={`w-4 h-4 ${overdue ? 'text-amber-400' : 'text-teal-400'}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {fmtLabel(referral?.specialtyId)}
          {referral?.packId && <span className="text-slate-400 font-normal"> · {fmtLabel(referral.packId)}</span>}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-xs text-slate-500 font-mono">{referral?.id ?? '—'}</span>
          {seen && <Badge tw="bg-purple-500/10 text-purple-400 border-purple-500/20" label="Seen" />}
          {PRIORITY_CONFIG[priority] && <Badge {...PRIORITY_CONFIG[priority]} />}
          {outcomeCfg && <Badge tw={outcomeCfg.tw} label={outcomeCfg.label} />}
          {overdue && (
            <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 font-medium">
              <AlertTriangle className="w-3 h-3" /> Needs closure
            </span>
          )}
        </div>
        <div className="mt-2 space-y-0.5">
          {apptDate ? (
            <p className={`text-xs flex items-center gap-1 ${overdue ? 'text-amber-400/70' : 'text-teal-300'}`}>
              <Calendar className="w-3 h-3" />
              {new Date(apptDate + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric',
              })}
              {apptTime && ` · ${from24h(apptTime) ?? apptTime}`}
            </p>
          ) : (
            <p className="text-xs text-slate-600 italic flex items-center gap-1">
              <Calendar className="w-3 h-3" /> No date set
            </p>
          )}
          {consultantName && (
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <User className="w-3 h-3" /> {consultantName}
            </p>
          )}
        </div>
      </div>

      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 flex-shrink-0 transition-colors mt-2" />
    </button>
  );
}

// ── Closed Referral Card ──────────────────────────────────────────────────────

function ClosedReferralCard({ item, onClick }) {
  const { referral, workflow } = item;
  const priority       = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;
  const finalOutcome   = referral?.finalOutcome ?? referral?.outcome?.status ?? null;
  const outcomeCfg     = finalOutcome ? (OUTCOME_CONFIG[finalOutcome] ?? null) : null;
  const closedAt       = workflow?.closedLoop?.closedAt;
  const consultantName = workflow?.scheduling?.consultantName ?? workflow?.scheduling?.consultantId ?? null;
  const apptDate       = workflow?.scheduling?.appointmentDate;

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-start gap-4 px-5 py-4 rounded-xl border transition-all group bg-slate-900/60 border-slate-700/50 hover:bg-slate-800/50 hover:border-slate-600/60"
    >
      <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {fmtLabel(referral?.specialtyId)}
          {referral?.packId && <span className="text-slate-400 font-normal"> · {fmtLabel(referral.packId)}</span>}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-xs text-slate-500 font-mono">{referral?.id ?? '—'}</span>
          {priority !== WORKFLOW_PRIORITY.NORMAL && PRIORITY_CONFIG[priority] && (
            <Badge {...PRIORITY_CONFIG[priority]} />
          )}
          {outcomeCfg && <Badge tw={outcomeCfg.tw} label={outcomeCfg.label} />}
        </div>
        <div className="mt-2 space-y-0.5">
          {closedAt && (
            <p className="text-xs text-emerald-300/70 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Closed {fmtTs(closedAt)}
            </p>
          )}
          {apptDate && (
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(apptDate + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric',
              })}
            </p>
          )}
          {consultantName && (
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <User className="w-3 h-3" /> {consultantName}
            </p>
          )}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 flex-shrink-0 transition-colors mt-2" />
    </button>
  );
}

// ── Tab Bar ───────────────────────────────────────────────────────────────────

function TabBar({ active, counts, onChange, activeFocusTarget }) {
  return (
    <div className="flex gap-1 border-b border-slate-800/60 mb-6">
      {TABS.map(({ id, label, activeColor, learningTarget }) => {
        const isActive = active === id;
        const count    = counts[id] ?? 0;
        const isHighlighted = activeFocusTarget && activeFocusTarget === learningTarget;
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            {...(learningTarget ? { 'data-learning-target': learningTarget } : {})}
            className={[
              'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
              isActive ? `${activeColor} bg-transparent` : 'text-slate-500 border-transparent hover:text-slate-300',
              isHighlighted ? 'ring-2 ring-teal-400/30 rounded-t-lg' : '',
            ].join(' ')}
          >
            {label}
            <span className={[
              'text-xs px-1.5 py-0.5 rounded-full font-semibold',
              isActive ? 'bg-slate-700/80' : 'bg-slate-800/60 text-slate-600',
              id === 'needs_action' && count > 0 && !isActive ? 'bg-amber-500/20 text-amber-400' : '',
            ].join(' ')}>
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Load More ─────────────────────────────────────────────────────────────────

function LoadMoreRow({ remaining, onLoad }) {
  return (
    <div className="pt-2 pb-1 flex flex-col items-center gap-1">
      <button type="button" onClick={onLoad}
        className="px-5 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/40 hover:border-slate-600/60 text-slate-400 hover:text-white text-sm font-medium transition-all">
        Load more
      </button>
      <p className="text-[11px] text-slate-600">{remaining} remaining</p>
    </div>
  );
}

// ── Tab empty state ───────────────────────────────────────────────────────────

function TabEmptyState({ tabId }) {
  const tab = TABS.find((t) => t.id === tabId);
  if (!tab) return null;
  const Icon = tab.emptyIcon;
  return (
    <div className="text-center py-16">
      <Icon className="w-10 h-10 text-slate-700 mx-auto mb-3" />
      <p className="text-slate-500 font-medium">{tab.emptyTitle}</p>
      <p className="text-slate-600 text-sm mt-1">{tab.emptyBody}</p>
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

export default function SpecialistWorklistView({ initialTab, activeFocusTarget, onLearningContext, autoOpenReferralId }) {
  const { user } = useAuth();
  const tenantId     = user?.tenantId;
  const uid          = user?.uid;
  const role         = user?.role ?? 'specialist';
  // Stable string key so useCallback dep doesn't change on every render
  const specialtyIds     = user?.specialtyIds ?? [];
  const specialtyIdsKey  = specialtyIds.slice().sort().join(',');

  const PAGE_SIZE = 25;

  // Default tab = Today
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(null);
  const [activeTab,    setActiveTab]    = useState(initialTab || 'today');
  const [selectedItem, setSelectedItem] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [seenItems,          setSeenItems]          = useState([]);

  // ── Closed Referrals tab state ──────────────────────────────────────────────
  const [closedItems,        setClosedItems]        = useState([]);
  const [closedLoaded,       setClosedLoaded]       = useState(false);
  const [closedLoading,      setClosedLoading]      = useState(false);
  const [closedError,        setClosedError]        = useState(null);
  const [closedVisibleCount, setClosedVisibleCount] = useState(PAGE_SIZE);

  // Reset pagination when switching tabs
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeTab]);
  useEffect(() => { setClosedVisibleCount(PAGE_SIZE); }, [activeTab]);

  const selectedIsSeen = selectedItem?.workflow?.status === WORKFLOW_STATUS.SEEN;
  const selectedIsUnreconciled = !!selectedItem
    && selectedItem?.workflow?.status === WORKFLOW_STATUS.SCHEDULED
    && (() => {
      const d = selectedItem?.workflow?.scheduling?.appointmentDate;
      return typeof d === 'string' && d.slice(0, 10) < todayLocal();
    })();

  useEffect(() => {
    if (onLearningContext) {
      onLearningContext({
        specialistTabToday: activeTab === 'today',
        specialistTabUpcoming: activeTab === 'upcoming',
        specialistTabNeedsAction: activeTab === 'needs_action',
        specialistTabClosedReferrals: activeTab === 'closed_referrals',
        specialistClosingDrawer: !!selectedItem && selectedIsSeen,
        specialistUnreconciledDrawer: selectedIsUnreconciled,
        specialistTodayDrawer: activeTab === 'today' && !!selectedItem && !selectedIsSeen && !selectedIsUnreconciled,
      });
    }
    return () => { if (onLearningContext) onLearningContext({}); };
  }, [activeTab, selectedItem, selectedIsSeen, selectedIsUnreconciled, onLearningContext]);

  const load = useCallback(async () => {
    if (!tenantId || !uid) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listWorkqueueItems(tenantId, { pageSize: 200 });
      const mySpecialtyIds = specialtyIdsKey ? specialtyIdsKey.split(',') : [];
      // specialist_admin sees all consultants in their specialties (no consultantId gate).
      // specialist sees only their own appointments (consultantId === uid).
      const isAdmin = role === 'specialist_admin';
      const mine = data.filter(({ referral, workflow }) =>
        (workflow?.status === WORKFLOW_STATUS.SCHEDULED || workflow?.status === WORKFLOW_STATUS.SEEN) &&
        (mySpecialtyIds.length === 0 || mySpecialtyIds.includes(referral?.specialtyId)) &&
        (isAdmin || workflow?.scheduling?.consultantId === uid)
      );
      setItems(mine.filter(({ workflow }) => workflow?.status === WORKFLOW_STATUS.SCHEDULED));
      setSeenItems(mine.filter(({ workflow }) => workflow?.status === WORKFLOW_STATUS.SEEN));
    } catch (err) {
      console.error('[SpecialistWorklist] load failed:', err);
      setLoadError(safeUserError(err) ?? 'Failed to load specialist worklist. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, uid, role, specialtyIdsKey]);

  useEffect(() => { load(); }, [load]);

  // Deep-link: open the exact referral when arriving from "Open Referral" (Referral
  // Pings). Fires once after the worklist loads; opens the ConsultDetailDrawer overlay
  // (active tab is irrelevant since the drawer overlays). Shows a notice if not found.
  const [deepLinkNotice, setDeepLinkNotice] = useState('');
  const autoOpenFired = useRef(false);
  useEffect(() => {
    if (!autoOpenReferralId || autoOpenFired.current || loading) return;
    autoOpenFired.current = true;
    const res = findReferralInBuckets([
      { tab: 'today', items },
      { tab: 'needs_action', items: seenItems },
    ], autoOpenReferralId);
    if (res.found) {
      if (res.item.workflow?.status === WORKFLOW_STATUS.SEEN) setActiveTab('needs_action');
      setSelectedItem(res.item);
    } else {
      // Not in this specialist's worklist — read-only fallback (non-PHI core) for
      // a specific reason instead of a vague "not available".
      getReferral(tenantId, autoOpenReferralId)
        .then((ref) => setDeepLinkNotice(classifyDeepLinkFailure({ referral: ref })))
        .catch((err) => setDeepLinkNotice(classifyDeepLinkFailure({ error: err })));
    }
  }, [autoOpenReferralId, loading, items, seenItems, tenantId]);

  // Patch item in local state after a workflow action.
  // keepOpen = true  → status updated to SEEN in place, drawer stays open.
  // keepOpen = false → item removed from worklist (closed), drawer dismissed.
  const handleCompleted = useCallback((referralId, keepOpen = false) => {
    if (keepOpen) {
      // Move the item from SCHEDULED (items) into SEEN (seenItems) so the
      // Seen tab immediately reflects it, and update selectedItem so the
      // drawer re-renders without "Mark as Seen" and with "Close Referral Loop" only.
      const updatedWorkflow = (workflow) => ({
        ...workflow,
        status: WORKFLOW_STATUS.SEEN,
        scheduling: { ...workflow?.scheduling, seenAt: new Date() },
      });

      setItems((prev) => {
        const moved = prev.find((item) => item.referral?.id === referralId);
        if (moved) {
          setSeenItems((s) => [
            { ...moved, workflow: updatedWorkflow(moved.workflow) },
            ...s,
          ]);
        }
        return prev.filter((item) => item.referral?.id !== referralId);
      });

      setSelectedItem((prev) =>
        prev?.referral?.id === referralId
          ? { ...prev, workflow: updatedWorkflow(prev.workflow) }
          : prev
      );
    } else {
      // Item closed (CLOSED_LOOP) — remove from both active arrays and dismiss drawer.
      setItems((prev) => prev.filter((item) => item.referral?.id !== referralId));
      setSeenItems((prev) => prev.filter((item) => item.referral?.id !== referralId));
      setSelectedItem(null);
    }
  }, []);

  // ── Load closed referrals (lazy — only when tab is first opened) ──────────
  const loadClosed = useCallback(async () => {
    if (!tenantId || !uid) return;
    setClosedLoading(true);
    setClosedError(null);
    try {
      const data = await listWorkqueueItems(tenantId, { pageSize: 200 });
      const mySpecialtyIds = specialtyIdsKey ? specialtyIdsKey.split(',') : [];
      const isAdmin = role === 'specialist_admin';
      setClosedItems(sortClosed(data.filter(({ referral, workflow }) =>
        workflow?.status === WORKFLOW_STATUS.CLOSED_LOOP &&
        (mySpecialtyIds.length === 0 || mySpecialtyIds.includes(referral?.specialtyId)) &&
        (isAdmin || workflow?.scheduling?.consultantId === uid)
      )));
      setClosedLoaded(true);
    } catch (err) {
      console.error('[SpecialistWorklist] loadClosed failed:', err);
      setClosedError(safeUserError(err) ?? 'Failed to load closed referrals. Please retry.');
    } finally {
      setClosedLoading(false);
    }
  }, [tenantId, uid, role, specialtyIdsKey]);

  // Lazy load on first open; reset page on tab switch.
  useEffect(() => { if (activeTab === 'closed_referrals' && !closedLoaded) loadClosed(); }, [activeTab, closedLoaded, loadClosed]);

  // ── Partition into tabs ───────────────────────────────────────────────────
  const todayItems       = [...items.filter((i) => classifyItem(i) === 'today')]       .sort(sortToday);
  const unreconciledItems = [...items.filter((i) => classifyItem(i) === 'needs_action')].sort(sortNeedsAction);
  const pendingNotesItems = seenItems.filter((i) => !i.workflow?.closedLoop?.closedAt);
  const upcomingItems     = [...items.filter((i) => classifyItem(i) === 'upcoming')]    .sort(sortUpcoming);

  const tabCounts = {
    today:            todayItems.length,
    needs_action:     unreconciledItems.length + pendingNotesItems.length,
    upcoming:         upcomingItems.length,
    closed_referrals: closedItems.length,
  };

  const activeItems = {
    today:    todayItems,
    upcoming: upcomingItems,
  }[activeTab] ?? [];

  const visibleItems   = activeItems.slice(0, visibleCount);
  const remainingCount = activeItems.length - visibleCount;

  // readOnly = true for any CLOSED_LOOP item (opened from the Closed Referrals tab).
  // Active worklist items are SCHEDULED and eligible for completion.
  const selectedIsCompleted = selectedItem?.workflow?.status === WORKFLOW_STATUS.CLOSED_LOOP;

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-teal-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Specialist Worklist</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Review upcoming appointments and complete referrals.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-white text-sm transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-teal-400 animate-spin mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Loading specialist worklist…</p>
          </div>
        </div>
      )}

      {/* Load error */}
      {!loading && loadError && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-300 font-medium">Failed to load specialist worklist</p>
            <p className="text-xs text-red-400/80 mt-0.5">{loadError}</p>
            <button onClick={load} className="mt-2 text-xs text-red-300 underline hover:no-underline">Retry</button>
          </div>
        </div>
      )}

      {deepLinkNotice && (
        <div className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="flex-1 text-sm text-amber-300">{deepLinkNotice}</p>
          <button onClick={() => setDeepLinkNotice('')} className="text-xs text-amber-400/80 hover:text-amber-300">Dismiss</button>
        </div>
      )}

      {/* Tabs + Content */}
      {!loading && !loadError && (
        <>
          <TabBar active={activeTab} counts={tabCounts} onChange={setActiveTab} activeFocusTarget={activeFocusTarget} />

          {/* Today / Upcoming tabs */}
          {(activeTab === 'today' || activeTab === 'upcoming') && (
            <div className="space-y-2">
              {activeItems.length === 0 ? (
                <TabEmptyState tabId={activeTab} />
              ) : (
                <>
                  {visibleItems.map((item, idx) => (
                    <ReferralCard
                      key={item.referral.id}
                      item={item}
                      overdue={false}
                      highlightFirst={activeTab === 'today' && idx === 0 && activeFocusTarget === 'specialist-referral-card'}
                      onClick={() => setSelectedItem(item)}
                    />
                  ))}
                  {remainingCount > 0 && (
                    <LoadMoreRow
                      remaining={remainingCount}
                      onLoad={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* Needs Action tab — two sections */}
          {activeTab === 'needs_action' && (
            <div className="space-y-6">
              {unreconciledItems.length === 0 && pendingNotesItems.length === 0 && (
                <TabEmptyState tabId="needs_action" />
              )}

              {unreconciledItems.length > 0 && (
                <div
                  data-learning-target="specialist-unreconciled-section"
                  className={activeFocusTarget === 'specialist-unreconciled-section' ? 'ring-2 ring-red-400/20 rounded-xl p-2 -m-2 transition-all duration-300' : ''}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Flame className="w-3.5 h-3.5 text-red-400" />
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-red-400">
                      Unreconciled Visits
                    </h3>
                    <span className="text-xs text-red-400/60">({unreconciledItems.length})</span>
                  </div>
                  <div className="space-y-2">
                    {unreconciledItems.map((item) => (
                      <ReferralCard
                        key={item.referral.id}
                        item={item}
                        overdue={true}
                        onClick={() => setSelectedItem(item)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {pendingNotesItems.length > 0 && (
                <div
                  data-learning-target="specialist-pending-notes-section"
                  className={activeFocusTarget === 'specialist-pending-notes-section' ? 'ring-2 ring-purple-400/20 rounded-xl p-2 -m-2 transition-all duration-300' : ''}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-3.5 h-3.5 text-purple-400" />
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-purple-400">
                      Pending Consult Notes
                    </h3>
                    <span className="text-xs text-purple-400/60">({pendingNotesItems.length})</span>
                  </div>
                  <div className="space-y-2">
                    {pendingNotesItems.map((item) => (
                      <ReferralCard
                        key={item.referral.id}
                        item={item}
                        overdue={false}
                        seen={true}
                        onClick={() => setSelectedItem(item)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Closed Referrals tab */}
          {activeTab === 'closed_referrals' && (
            <>
              {closedLoading && (
                <div className="flex items-center justify-center py-24">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">Loading closed referrals…</p>
                  </div>
                </div>
              )}
              {!closedLoading && closedError && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-red-300 font-medium">Failed to load closed referrals</p>
                    <p className="text-xs text-red-400/80 mt-0.5">{closedError}</p>
                    <button onClick={loadClosed} className="mt-2 text-xs text-red-300 underline hover:no-underline">Retry</button>
                  </div>
                </div>
              )}
              {!closedLoading && !closedError && closedItems.length === 0 && (
                <TabEmptyState tabId="closed_referrals" />
              )}
              {!closedLoading && !closedError && closedItems.length > 0 && (
                <div className="space-y-2">
                  {closedItems.slice(0, closedVisibleCount).map((item) => (
                    <ClosedReferralCard
                      key={item.referral.id}
                      item={item}
                      onClick={() => setSelectedItem(item)}
                    />
                  ))}
                  {closedItems.length > closedVisibleCount && (
                    <LoadMoreRow
                      remaining={closedItems.length - closedVisibleCount}
                      onLoad={() => setClosedVisibleCount((c) => c + PAGE_SIZE)}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Consult Detail Drawer */}
      {selectedItem && (
        <ConsultDetailDrawer
          item={selectedItem}
          uid={uid}
          role={role}
          onClose={() => setSelectedItem(null)}
          onCompleted={handleCompleted}
          readOnly={selectedIsCompleted}
          activeFocusTarget={activeFocusTarget}
        />
      )}

    </div>
  );
}
